import { ComponentProps } from 'react';
import slugify from 'slugify';

import {
  IContentfulSection,
  IElementAccordionRow,
  Document,
  IPlanCardV2,
  IPlanCard,
  Entry,
  IDictionary
} from '@belong/types';
import {
  FMoleculeCallout,
  FMoleculeAlert,
  FElementAccordionRow,
  parseLinkData,
  FFeature,
  FAccordionRow,
  FRichText,
  FSystemIcon
} from '@belong/contentful';
import { ACCENTSTRIP_VARIANTS, PlanCard } from '@belong/ui-components';
import { getLegacyExpiryDate } from '@belong/nbn-utils';

import { FProduct } from '../../factories';

export type IPlanCardUnion = IPlanCard | IPlanCardV2;

export type ICommonPlanCardProps = Pick<
  ComponentProps<typeof PlanCard>,
  | 'accordionRefs'
  | 'accordionRowContentRefs'
  | 'alertRefs'
  | 'alertHeight'
  | 'removeAlertHeight'
  | 'activityLevelRefs'
  | 'activityLevelHeight'
  | 'removeActivityLevelHeight'
  | 'disclaimerRefs'
  | 'disclaimerHeight'
  | 'removeDisclaimerHeight'
  | 'calloutHeight'
  | 'calloutRefs'
  | 'removeCalloutHeight'
  | 'focusTextOnSelect'
  | 'hasTopPadding'
  | 'planGroupIncludesPromo'
  | 'toggleAccordions'
  | 'isPreSq'
>;

export function isIPlanCard(obj: IPlanCardUnion): obj is IPlanCard {
  return obj.contentType === 'organismPlanCard';
}

export enum PlanLayout {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical'
}

export interface ISectionPlanSummary extends IContentfulSection<'sectionPlanSummary'> {
  accordionRows?: IElementAccordionRow[];
  footerContent?: Document;
  planLayout?: PlanLayout;
  // @TODO - this will be IPlanCard once PlanCard uplift is done
  plans: IPlanCardUnion[];
  carousel?: boolean;
  sectionStyles?: any;
  focusTextOnSelect?: boolean;
  isPreSq?: boolean;
}

export const ACCENTSTRIP_DEFAULT = ACCENTSTRIP_VARIANTS.BLUE;

const FPlanCardElement = (data: any): IPlanCardV2 => {
  const {
    accentStripColour,
    calloutSubCopy,
    ctaLabel,
    ctaUrl,
    features,
    infoText,
    planCallout,
    planDifferentiator,
    planDifferentiatorColour,
    planSpeedDifferentiator,
    planSpeedDifferentiatorColour,
    product,
    supportingCopy,
    uploadSpeedLabel,
    downloadSpeedLabel
  } = data.fields;

  return {
    accentStripColour: accentStripColour || ACCENTSTRIP_DEFAULT,
    calloutSubCopy,
    ctaUrl: ctaUrl && parseLinkData(ctaUrl),
    features: features && features.map(feature => FFeature(feature)),
    infoText: infoText && FMoleculeAlert(infoText),
    plan: FProduct(product),
    planCallout: planCallout && FMoleculeCallout(planCallout),
    planDifferentiator,
    planDifferentiatorColour,
    planSpeedDifferentiator,
    planSpeedDifferentiatorColour,
    primaryButtonText: ctaLabel,
    supportingCopy,
    uploadSpeedLabel,
    downloadSpeedLabel,
    contentId: data.sys.id,
    contentType: 'elementPlanCard'
  };
};

const FSectionPlanSummary = (data: any): ISectionPlanSummary => {
  const { accordionRows, plans, carousel, footerContent, sectionStyles, identifier } = data.fields;

  return {
    accordionRows: accordionRows?.map?.(x => FElementAccordionRow(x)),
    contentType: 'sectionPlanSummary',
    footerContent,
    id: data.sys.id,
    identifier: slugify(identifier),
    plans: plans.map(plan => FPlanCard(plan)),
    carousel,
    sectionStyles
  };
};

export const FPlanCardOrganism = (data: any): IPlanCard => {
  const {
    planType,
    differentiatorLabel,
    differentiatorIcon,
    cardStripColour,
    title,
    product,
    dataBeforePromo,
    priceBeforePromo,
    promoCallout,
    typicalSpeedsCopy,
    alert,
    inclusions,
    addOns,
    suitability,
    disclaimer,
    activityLevel,
    buttonLabel
  } = data.fields;

  const plan = FProduct(product);
  const cardAlertDictionary: IDictionary = {
    expiryDate: getLegacyExpiryDate(plan.expiryDate),
    futurePrice: plan.futurePrice
  };

  return {
    contentType: 'organismPlanCard',
    planType,
    differentiatorLabel,
    differentiatorIcon: differentiatorIcon && FSystemIcon(differentiatorIcon),
    cardStripColour: cardStripColour || ACCENTSTRIP_DEFAULT,
    title,
    plan,
    dataBeforePromo,
    priceBeforePromo,
    promoCallout: promoCallout && FMoleculeCallout(promoCallout),
    typicalSpeedsCopy: typicalSpeedsCopy && FRichText(typicalSpeedsCopy),
    planCardAlert: alert && {
      ...FMoleculeAlert(alert),
      dictionary: cardAlertDictionary
    },
    inclusions: inclusions && inclusions.map(FAccordionRow),
    addOns: addOns && addOns.map(FAccordionRow),
    suitability: suitability && suitability.map(FAccordionRow),
    disclaimer: disclaimer && FRichText(disclaimer),
    activityLevel: activityLevel && FRichText(activityLevel),
    buttonLabel
  };
};

const FPlanCard = (plan: Entry<any>): IPlanCardUnion => {
  const contentType = plan.sys.contentType.sys.id;
  return contentType === 'organismPlanCard' ? FPlanCardOrganism(plan) : FPlanCardElement(plan);
};

export default FSectionPlanSummary;
